/**
 * Tile collision resolution.
 *
 * Resolution is axis-separated (move X, resolve; move Y, resolve) which is how
 * the original games do it and is what makes running along the floor feel
 * smooth — you never get snagged on a seam between two tiles.
 *
 * All positions and sizes are native pixels, floats allowed for sub-pixel
 * physics. Entities are axis-aligned boxes; slopes are handled by projecting
 * the entity onto the diagonal surface rather than by box collision.
 */

import { TILE } from './constants'

/** Slope direction, by the tile's high corner. */
export const enum Slope {
  NONE = 0,
  /** High on the right: surface rises left->right. */
  LEFT = 1,
  /** High on the left: surface falls left->right. */
  RIGHT = 2,
}

/** The subset of level queries collision needs. */
export interface SolidQuery {
  /** Blocks movement from any direction. */
  isSolid(tx: number, ty: number): boolean
  /** Blocks a downward landing only. */
  isOneWay(tx: number, ty: number): boolean
  /** Which slope, if any, occupies this tile. */
  slopeAt(tx: number, ty: number): Slope
  /**
   * Slippery surface: standing on this tile swaps the player to the low-grip
   * profile. Optional, because a slippery tile is a property of a *placed
   * object* rather than of the terrain layer, so the many worlds that only
   * answer "is this solid" (every test double, the plain collision helpers)
   * stay valid.
   */
  isIce?(tx: number, ty: number): boolean
}

/** A movable box in the world. */
export interface Body {
  x: number
  y: number
  w: number
  h: number
  vx: number
  vy: number
  onGround: boolean
  onCeiling: boolean
  onWall: 1 | -1 | 0
  /** Set false to ignore one-way platforms for this step (e.g. when dropping through). */
  ignoreOneWay: boolean
}

export function makeBody(x: number, y: number, w: number, h: number): Body {
  return {
    x,
    y,
    w,
    h,
    vx: 0,
    vy: 0,
    onGround: false,
    onCeiling: false,
    onWall: 0,
    ignoreOneWay: false,
  }
}

/**
 * Move a body horizontally, stopping at solid tiles.
 * Returns true if it was blocked.
 */
export function moveX(b: Body, world: SolidQuery, dx: number): boolean {
  if (dx === 0) return false
  b.x += dx
  const dir: 1 | -1 = dx > 0 ? 1 : -1

  const ty0 = Math.floor(b.y / TILE)
  const ty1 = Math.floor((b.y + b.h - 1) / TILE)

  if (dir > 0) {
    const tx = Math.floor((b.x + b.w - 1) / TILE)
    for (let ty = ty0; ty <= ty1; ty++) {
      if (world.isSolid(tx, ty)) {
        b.x = tx * TILE - b.w
        b.vx = 0
        b.onWall = 1
        return true
      }
    }
  } else {
    const tx = Math.floor(b.x / TILE)
    for (let ty = ty0; ty <= ty1; ty++) {
      if (world.isSolid(tx, ty)) {
        b.x = (tx + 1) * TILE
        b.vx = 0
        b.onWall = -1
        return true
      }
    }
  }
  b.onWall = 0
  return false
}

/**
 * Vertical sub-step epsilon, in pixels.
 *
 * The downward collision test uses `y + h - 1`, so a body resting exactly on a
 * floor does not register as colliding: its feet sit at the boundary, and the
 * test looks at the pixel *above* it. Gravity then integrates a fresh 0.3px
 * step every frame, re-collides, snaps back to the boundary, and the body
 * never reports `onGround` — it jitters on the spot and any AI keyed off
 * `onGround` misbehaves.
 *
 * The fix is to treat a sub-pixel move toward a surface as a collision, which
 * is what the original games' fixed-point integrators effectively do. Half a
 * pixel is well under the smallest movement any of our entities produces.
 */
const SKIN = 0.5

/**
 * Move a body vertically, stopping at solid tiles and one-way platforms.
 * Returns true if it was blocked.
 */
export function moveY(b: Body, world: SolidQuery, dy: number): boolean {
  if (dy === 0) {
    // Still need to detect standing on a platform while stationary.
    return probeGround(b, world)
  }
  b.y += dy
  const dir: 1 | -1 = dy > 0 ? 1 : -1

  const tx0 = Math.floor(b.x / TILE)
  const tx1 = Math.floor((b.x + b.w - 1) / TILE)

  if (dir > 0) {
    // Use the exact foot line, not `y + h - 1`, and treat "within SKIN of the
    // tile below" as contact. See the SKIN comment above.
    const footY = b.y + b.h
    const ty = Math.floor(footY / TILE)
    const penetration = footY - ty * TILE
    const nearFloor =
      penetration <= SKIN && ty !== Math.floor((b.y + b.h - 1) / TILE)
    for (let tx = tx0; tx <= tx1; tx++) {
      const blocking =
        world.isSolid(tx, ty) || (!b.ignoreOneWay && world.isOneWay(tx, ty))
      if (blocking && (penetration > 0 || nearFloor)) {
        b.y = ty * TILE - b.h
        b.vy = 0
        b.onGround = true
        return true
      }
    }
  } else {
    const ty = Math.floor(b.y / TILE)
    for (let tx = tx0; tx <= tx1; tx++) {
      if (world.isSolid(tx, ty)) {
        b.y = (ty + 1) * TILE
        b.vy = 0
        b.onCeiling = true
        return true
      }
    }
  }
  b.onGround = false
  b.onCeiling = false
  return false
}

/** Zero-velocity ground probe, so `onGround` stays true when standing still. */
function probeGround(b: Body, world: SolidQuery): boolean {
  const ty = Math.floor((b.y + b.h) / TILE)
  const tx0 = Math.floor(b.x / TILE)
  const tx1 = Math.floor((b.x + b.w - 1) / TILE)
  for (let tx = tx0; tx <= tx1; tx++) {
    if (world.isSolid(tx, ty) || (!b.ignoreOneWay && world.isOneWay(tx, ty))) {
      b.onGround = true
      return true
    }
  }
  // A slope counts as ground if the body rests on it.
  if (slopeSupport(b, world) !== null) {
    b.onGround = true
    return true
  }
  b.onGround = false
  return false
}

/**
 * The surface Y of a slope tile at a given world X, or null if the body does
 * not overlap a slope there. Used to glue the body to a 45-degree ramp.
 */
function slopeSupport(
  b: Body,
  world: SolidQuery,
): { y: number; slope: Slope } | null {
  const ty0 = Math.floor(b.y / TILE)
  const ty1 = Math.floor((b.y + b.h - 1) / TILE)
  for (let ty = ty0; ty <= ty1; ty++) {
    const tx0 = Math.floor(b.x / TILE)
    const tx1 = Math.floor((b.x + b.w - 1) / TILE)
    for (let tx = tx0; tx <= tx1; tx++) {
      const slope = world.slopeAt(tx, ty)
      if (slope === Slope.NONE) continue
      // Only the very top of the body rides the slope.
      const footX = b.x + b.w / 2
      const frac = (footX - tx * TILE) / TILE
      // LEFT  = high on right  => surface y goes from bottom-left to top-right
      // RIGHT = high on left   => surface y goes from top-left to bottom-right
      const localY = slope === Slope.LEFT ? 1 - frac : frac
      return { y: ty * TILE + localY * TILE, slope }
    }
  }
  return null
}

/**
 * Snap a body sitting on a slope so its feet follow the diagonal.
 * Call after `moveX` when grounded.
 */
export function applySlope(b: Body, world: SolidQuery): void {
  if (!b.onGround) return
  const sup = slopeSupport(b, world)
  if (sup === null) return
  const targetY = sup.y - b.h
  // Only correct downwards-to-ramp and gently upwards, to avoid popping.
  if (targetY > b.y) {
    b.y = targetY
  } else if (targetY < b.y && b.y - targetY <= 1.5) {
    b.y = targetY
  }
  if (b.vy > 0) b.vy = 0
}

/** Full step: horizontal then vertical, with slope correction. */
export function stepBody(b: Body, world: SolidQuery): void {
  b.onGround = false
  b.onCeiling = false
  b.onWall = 0
  moveX(b, world, b.vx)
  moveY(b, world, b.vy)
  applySlope(b, world)
}

/** A solid that is not on the tile grid: a moving platform, in pixel space. */
export interface DynamicSolidRect {
  x: number
  y: number
  w: number
  h: number
}

/**
 * Resolve a body against free-floating solid rectangles.
 *
 * Moving platforms are the reason this exists. A platform is 16px tall and
 * travels vertically, so while it is between tile boundaries it straddles two
 * rows; baking it into the tile grid makes it two tiles tall half the time, and
 * a rider standing on its true top surface ends up embedded in the upper tile
 * and gets shoved up a whole tile. Colliding as a rectangle instead means the
 * platform is exactly as tall as it looks, at any position.
 *
 * The body is placed after resolution has already run against the tile grid, so
 * this is a corrective pass: it separates any overlap by the shortest axis,
 * biased towards landing on top, which is what a rider almost always wants.
 *
 * Returns the number of rectangles the body ended up resting on, so callers
 * can treat a ride as ground contact.
 */
export function resolveDynamic(b: Body, solids: DynamicSolidRect[]): number {
  let resting = 0
  for (let pass = 0; pass < 2; pass++) {
    for (const r of solids) {
      if (
        b.x >= r.x + r.w ||
        b.x + b.w <= r.x ||
        b.y >= r.y + r.h ||
        b.y + b.h <= r.y
      ) {
        continue
      }
      // Overlap on each axis.
      const overlapUp = b.y + b.h - r.y // push body up
      const overlapDown = r.y + r.h - b.y // push body down
      const overlapLeft = b.x + b.w - r.x
      const overlapRight = r.x + r.w - b.x

      const smallest = Math.min(
        overlapUp,
        overlapDown,
        overlapLeft,
        overlapRight,
      )
      if (smallest === overlapUp) {
        b.y = r.y - b.h
        if (b.vy > 0) b.vy = 0
        b.onGround = true
        resting++
      } else if (smallest === overlapDown) {
        b.y = r.y + r.h
        if (b.vy < 0) b.vy = 0
        b.onCeiling = true
      } else if (smallest === overlapLeft) {
        b.x = r.x - b.w
        if (b.vx > 0) b.vx = 0
        b.onWall = -1
      } else {
        b.x = r.x + r.w
        if (b.vx < 0) b.vx = 0
        b.onWall = 1
      }
    }
  }
  return resting
}
